import { createContext, useContext, useEffect, useState } from "react";
import { getTokenPayload } from "../utils/token";

interface AuthContextProps {
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  country: string | null;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);
const EXPECTED_PORTAL = 'manager';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [country, setCountry] = useState<string | null>(null);

  const validatePortalAccess = (tokenToValidate: string): boolean => {
    try {
      const payload = getTokenPayload(tokenToValidate);
      return payload?.Portal === EXPECTED_PORTAL;
    } catch (error) {
      console.error('Error validando portal:', error);
      return false;
    }
  };

  const login = (newToken: string) => {
    if (!validatePortalAccess(newToken)) {
      console.error('Token no válido para este portal');
      logout();
      return;
    }
    const payload = getTokenPayload(newToken);
    setToken(newToken);
    setCountry(payload.CountryCode);
    localStorage.setItem("token", newToken);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setCountry(null);
  };

  const isAuthenticated = !!token;
  const isValidPortal = token ? validatePortalAccess(token) : false;


  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      if (validatePortalAccess(storedToken)) {
        const payload = getTokenPayload(storedToken);
        setToken(storedToken);
        setCountry(payload.CountryCode);
      } else {
        localStorage.removeItem("token");
      }
    }
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ token, login, logout, isAuthenticated: isAuthenticated && isValidPortal, isLoading, country }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("Error");
  }

  return context;
};
